/* eslint-disable @typescript-eslint/no-explicit-any */
import { Alert, Box, Button, Stack, TextField, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Dispatch, SetStateAction, useState, useEffect } from 'react';
import { fetchSignInMethodsForEmail, getAuth } from 'firebase/auth';
import { blackText, secondary, straightFont, white } from '../../../Constants';
import GenericDialog from '../../GenericDialog';
import { useAuth } from '../../../contexts/AuthContext';

// Shared text field styling
const textFieldSx = {
  width: '100%',
  '& .MuiOutlinedInput-root': {
    fontFamily: straightFont,
    '& fieldset': {
      borderColor: alpha(blackText, 0.2),
    },
    '&:hover fieldset': {
      borderColor: alpha(blackText, 0.4),
    },
    '&.Mui-focused fieldset': {
      borderColor: secondary,
      borderWidth: '2px',
    },
  },
  '& .MuiInputLabel-root': {
    fontFamily: straightFont,
    color: alpha(blackText, 0.6),
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: secondary,
  },
};

interface ISignupProps {
  showSignup: boolean;
  setShowSignup: Dispatch<SetStateAction<boolean>>;
  setShowLogin: Dispatch<SetStateAction<boolean>>;
}

export default function Signup(props: ISignupProps) {
  const { showSignup, setShowSignup, setShowLogin } = props;
  const [displayName, setDisplayName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const { signup } = useAuth();

  const closeModal = () => {
    setShowSignup(false);
  };

  const handleSubmit = async () => {
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    try {
      setError('');
      setLoading(true);

      // Check if email already exists
      const auth = getAuth();
      const signInMethods = await fetchSignInMethodsForEmail(auth, email);
      if (signInMethods.length > 0) {
        setError('An account with this email already exists');
        setLoading(false);
        return;
      }

      await signup(email, password, displayName);
      setShowSignup(false);
    } catch (err: any) {
      setError(err.message);
    }
    setLoading(false);
  };

  const handleLogin = () => {
    closeModal();
    setShowLogin(true);
  };

  useEffect(() => {
    if (showSignup) {
      setError('');
    }
  }, [showSignup]);

  return (
    <GenericDialog open={showSignup} closeModal={closeModal} isLoading={loading}>
      <Box
        component="form"
        sx={{
          p: 4,
          maxWidth: 400,
          backgroundColor: white,
        }}
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
      >
        {/* Header */}
        <Typography
          variant="h5"
          sx={{
            fontFamily: straightFont,
            fontWeight: 600,
            color: blackText,
            textAlign: 'center',
            mb: 1,
          }}
        >
          Create Account
        </Typography>
        <Typography
          sx={{
            fontFamily: straightFont,
            fontSize: '0.9rem',
            color: alpha(blackText, 0.6),
            textAlign: 'center',
            mb: 3,
          }}
        >
          Join us to save your favorite recipes
        </Typography>

        {/* Form Fields */}
        <Stack spacing={2}>
          <TextField
            type="text"
            label="Display Name"
            name="displayName"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            sx={textFieldSx}
            variant="outlined"
            size="medium"
            fullWidth
          />
          <TextField
            type="email"
            label="Email"
            name="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            sx={textFieldSx}
            variant="outlined"
            size="medium"
            fullWidth
          />
          <TextField
            type="password"
            name="password"
            label="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            sx={textFieldSx}
            variant="outlined"
            size="medium"
            fullWidth
          />
          <TextField
            type="password"
            name="confirmPassword"
            label="Confirm Password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            sx={textFieldSx}
            variant="outlined"
            size="medium"
            fullWidth
          />
        </Stack>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        {/* Buttons */}
        <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
          <Button
            variant="outlined"
            onClick={closeModal}
            fullWidth
            sx={{
              color: alpha(blackText, 0.7),
              borderColor: alpha(blackText, 0.2),
              fontFamily: straightFont,
              fontWeight: 500,
              py: 1.25,
              textTransform: 'none',
              '&:hover': {
                borderColor: alpha(blackText, 0.4),
                backgroundColor: alpha(blackText, 0.04),
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            type="submit"
            fullWidth
            sx={{
              backgroundColor: secondary,
              fontFamily: straightFont,
              fontWeight: 600,
              py: 1.25,
              textTransform: 'none',
              boxShadow: `0 4px 14px ${alpha(secondary, 0.35)}`,
              '&:hover': {
                backgroundColor: alpha(secondary, 0.9),
                boxShadow: `0 6px 20px ${alpha(secondary, 0.45)}`,
              },
            }}
          >
            Sign Up
          </Button>
        </Stack>

        {/* Footer Link */}
        <Box sx={{ mt: 3, pt: 2, borderTop: `1px solid ${alpha(blackText, 0.1)}`, textAlign: 'center' }}>
          <Button
            variant="text"
            onClick={handleLogin}
            sx={{
              fontFamily: straightFont,
              fontSize: '0.875rem',
              color: alpha(blackText, 0.6),
              textTransform: 'none',
              '&:hover': { backgroundColor: alpha(blackText, 0.04) },
            }}
          >
            Already have an account?{' '}
            <Typography component="span" sx={{ color: secondary, ml: 0.5, fontWeight: 600 }}>
              Login
            </Typography>
          </Button>
        </Box>
      </Box>
    </GenericDialog>
  );
}
